import { FC } from 'react';
import { useMutation } from 'react-query';
import * as Yup from 'yup';

import { MyProfile } from 'MyProfile/model/MyProfile';
import { myProfileApi } from 'MyProfile/service/myProfile.api';
import { ApiError } from 'model/ApiError';
import { EditUserForm } from 'MyProfile/component/EditUserForm';
import { useToasts } from 'components/ToastProvider';
import {
  EditUserFormData,
  mapEditUserDto,
} from 'MyProfile/component/EditUserForm/EditUserForm';
import { queryClient } from 'service';
import { userKey, useMyProfileQuery } from 'MyProfile/query/myProfile.query';

export interface EditInternalUserFormProps {
  user: MyProfile;
}

export const formValidationSchema = Yup.object().shape({
  manager: Yup.object().typeError('Select Manager'),
  businessUnit: Yup.object().typeError('Select Division'),
  preferredLanguages: Yup.array().min(1,'Select at least one language'),
  company: Yup.object().required().typeError('Select company'),
  employeeJobs: Yup.array().min(1, 'Select at least one Employee job'),
});

export const EditInternalUserForm: FC<EditInternalUserFormProps> = ({
  user,
}) => {
  const { showError, showSuccess } = useToasts();
  const { data: userData } = useMyProfileQuery();
  const localAdminRole = userData?.roles?.find((r) => r.type === 'LocalAdministrator');  

  const { mutate, isLoading } = useMutation<
    unknown,
    ApiError,
    EditUserFormData
  >((formData) => myProfileApi.updateUser(user.id, mapEditUserDto(formData)), {
    onSuccess: () => {
      showSuccess('User profile has been updated');
      queryClient.invalidateQueries(userKey);
    },
    onError: (error) => showError(error.message),
  });

  return (
    <EditUserForm
      user={user}
      countryId={user.country.id}
      loading={isLoading}
      validationSchema={formValidationSchema}
      onSubmit={async (values, { resetForm }) => {
        const selectedDivision = values.businessUnit?.name
        const selectedCountry = values.country?.name;                      
        let countSuccess = false;
        localAdminRole?.details.forEach((detail) => {
          const division = detail?.description1;
          const country = detail?.description2?.split('-')?.[0]?.trim();                

          if (division.trim() === selectedDivision?.trim()) { // false && false 
            if (country.toUpperCase()?.trim() === 'ALL COUNTRIES' || country === selectedCountry?.trim()) {
              countSuccess = true;              
            }
          }
        });
        if (!userData?.isAdmin && !countSuccess) { 
          showError(
            `The local admin cannot edit user of ${selectedDivision} division and from ${selectedCountry} .`
          );
          return;
        }       
        try {
          await mutate(values);
          resetForm({ values });
        } catch (e) {
          resetForm();
        }
      }}
    />
  );
};
